import * as H from 'history';
import { CombinedState } from '@reduxjs/toolkit';
import { IntlShape } from 'react-intl';
import format from 'date-fns/format';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import differenceInDays from 'date-fns/differenceInDays';
import {
  CondoRequestTypeEnum,
  ICondoSearchRequest,
  ICondoRequestFilters,
  SearchTypeEnum,
  ICondoStates,
  IDateRange,
  RatingEnum,
  IPlace,
  IRange,
  ICountriesCode,
  SupplierType,
  ICondoUnit,
} from 'src/common-types/index';
import {
  CONDO_UNITS_SEARCH_LABEL,
  DEFAULT_PAGE_SIZE,
  HOTEL_ID_ELEMENT,
  PACKAGE_ID_ELEMENT,
  DEFAULT_MAP_LIMIT,
  Routes,
  GUEST_TYPE_MEMBER,
  CONDO_UNITS_SESSION_KEY_LABEL,
  VideoExtensionEnum,
} from 'src/constants/index';
import {
  ICondoFlexibleDatePickerState,
  ICondoGuestsState,
  ICondosLocationPickerState,
  ICondosState,
  ICondoStrictDatesState,
  ICondoFiltersState,
  condoFiltersInitialState,
} from 'src/store/slices/index';
import { getCondoRequestType } from './condo-search';
import { UrlUtils } from './urls';
import { renderPhone } from './common';
import isUndefined from 'lodash/isUndefined';
import startCase from 'lodash/startCase';
import toLower from 'lodash/toLower';

const isoFormat = 'yyyy-MM-dd';
const startTime = 'T00:00:01.000Z';
const skipPages = 4;
const defaultDistance = 20;
const THREE = 3;
const ZERO = 0;
const FULL = 'Full';
const USA = 'USA';
const USA_FULL_NAME = 'United States of America';
const UNDEFINED = 'undefined';
const STAY_DURATION = 'week';

export const getCondoId = (): number => {
  return Number(location.pathname.split('/')[HOTEL_ID_ELEMENT]);
};

export const getAvailabilityId = (): string => {
  return location.pathname.split('/')[PACKAGE_ID_ELEMENT];
};

export const getFakeISODate = (dateString: string): string => {
  return format(new Date(dateString), isoFormat) + startTime;
};

export const getSeparatedFromTo = (num: string): IRange => {
  const [from, to]: string[] = num.split('-');

  return { from: +from || null, to: +to || null };
};

export const getSeparatedPlace = (num: string): IPlace => {
  const [city, state, country, title]: string[] = num.split('|');

  return state === UNDEFINED ? { city, country, title } : { city, state, country, title };
};

export const getCondoFilters = (condoFiltersStore: ICondoFiltersState): ICondoRequestFilters => {
  return {
    propertyName: '',
    budgetRanges: condoFiltersStore.budgetRanges.map((range: string) => {
      return getSeparatedFromTo(range);
    }),
    guestRatings: condoFiltersStore.guestRatings.map((num) => {
      return getSeparatedFromTo(num);
    }),
    distanceInMiles: condoFiltersStore.distanceInMiles,
    unitAmenities: condoFiltersStore.unitAmenities,
    facilities: condoFiltersStore.facilities,
    activities: condoFiltersStore.activities,
    condoTypes: condoFiltersStore.condoTypes,
    policies: [],
    accessibilities: condoFiltersStore.accessibilities,
    neighbourhoods: condoFiltersStore.neighbourhoods,
    places: condoFiltersStore.places.map((place) => {
      return getSeparatedPlace(place);
    }),
    bathrooms: condoFiltersStore.bathrooms,
    kitchenTypes: condoFiltersStore.kitchenTypes,
    isAllInclusiveOnly: condoFiltersStore.isAllInclusiveOnly,
  };
};

export const getCondoSearchBody = (
  searchType: SearchTypeEnum,
  getState: () => CombinedState<{
    condosStore: ICondosState;
    condosLocationPickerStore: ICondosLocationPickerState;
    condoGuestsStore: ICondoGuestsState;
    condoStrictDatesStore: ICondoStrictDatesState;
    condoFlexibleDatePickerStore: ICondoFlexibleDatePickerState;
    condoFiltersStore: ICondoFiltersState;
  }>,
  isReuse = false,
): ICondoSearchRequest => {
  const {
    condosStore,
    condosLocationPickerStore,
    condoGuestsStore,
    condoStrictDatesStore,
    condoFlexibleDatePickerStore,
    condoFiltersStore,
  } = getState();
  const { cachedSelectedLocation } = condosStore;
  const requestType: CondoRequestTypeEnum = getCondoRequestType(
    condoStrictDatesStore,
    condoFlexibleDatePickerStore,
  );
  const { startDate, endDate } = condoStrictDatesStore;
  const { selectedMonths } = condoFlexibleDatePickerStore;
  const res: ICondoSearchRequest = {
    filter: getCondoFilters(condoFiltersStore),
    bounds: condosStore.bounds ? condosStore.bounds : null,
    pageNumber:
      searchType === SearchTypeEnum.Pagination
        ? condosStore.pageNumber + skipPages
        : condosStore.pageNumber,
    pageSize: searchType === SearchTypeEnum.Pagination ? DEFAULT_PAGE_SIZE : DEFAULT_MAP_LIMIT,
    sessionKey: searchType !== SearchTypeEnum.NewSearch || isReuse ? condosStore.sessionKey : null,
    sortBy: condoFiltersStore.sortType,
  };

  if (searchType !== SearchTypeEnum.Pagination) {
    res.condosRequest = {
      location: condosLocationPickerStore.selectedLocation
        ? condosLocationPickerStore.selectedLocation
        : cachedSelectedLocation,
      adultsCount: condoGuestsStore.adultsCount,
      bedroomsCount: condoGuestsStore.bedroomsCount,
      childrenCount: condoGuestsStore.kidsCount,
      isPetsAllowed: condoGuestsStore.petsAllowed,
    };

    if (requestType === CondoRequestTypeEnum.Strict) {
      res.condosRequest.checkIn = startDate;
      res.condosRequest.checkOut = endDate;
    } else if (requestType === CondoRequestTypeEnum.Flexible) {
      res.condosRequest.stayDuration = STAY_DURATION;
      res.condosRequest.months = selectedMonths.map((dateString: string) => {
        return getFakeISODate(dateString);
      });
    }
  }

  return res;
};

export const onCondoPage = (condoId: number, history: H.History<H.LocationState>): void => {
  UrlUtils.removeFromUrl(CONDO_UNITS_SEARCH_LABEL);
  UrlUtils.removeFromUrl(CONDO_UNITS_SESSION_KEY_LABEL);

  history.push(`${Routes.Condo}/${condoId}/${location.search}`);
};

export const getMemberPhone = (memberType: string, phone: string): string => {
  return memberType === GUEST_TYPE_MEMBER ? renderPhone(phone) : '';
};

export const getState = (
  country: string,
  countriesCode: ICountriesCode,
  supplierType: string,
): ICondoStates[] => {
  if (supplierType === SupplierType.Rci) {
    return country
      ? countriesCode.rciCountries.find(
        (item) => item.name === (country === USA_FULL_NAME ? USA : country),
      ).states
      : [];
  } else {
    return country
      ? countriesCode.intervalCountries.find((item) => item.name === country).states
      : [];
  }
};

export const isPostalCode = (
  country: string,
  countriesCode: ICountriesCode,
  supplierType: string,
): boolean => {
  if (supplierType === SupplierType.Rci) {
    return countriesCode.rciCountries.some((item) => item.name === country && item.isZipRequired);
  } else {
    return countriesCode.intervalCountries.some(
      (item) => item.name === country && item.isStateRequired,
    );
  }
};

export const getNightsCount = (dateRange: IDateRange): number => {
  return differenceInDays(new Date(dateRange.to), new Date(dateRange.from));
};

export const getCondoTextRating = (rating: number): string => {
  if (rating >= RatingEnum.Excellent) {
    return 'rating.excellent';
  } else if (rating >= RatingEnum.Great) {
    return 'rating.great';
  } else if (rating >= RatingEnum.Good) {
    return 'rating.good';
  } else if (rating >= RatingEnum.Average) {
    return 'rating.average';
  } else if (rating >= RatingEnum.Poor) {
    return 'rating.poor';
  } else if (rating >= RatingEnum.Bad) {
    return 'rating.bad';
  }

  return 'rating.bad';
};

export const isVideo = (media: string): boolean => {
  return (
    media.substr(media.length - THREE) === VideoExtensionEnum.MOV ||
    media.substr(media.length - THREE) === VideoExtensionEnum.MP4
  );
};

export const hideBrowserAutoFill = (string: string, number: number): string => {
  return string.substr(ZERO, number) + '\u200e' + string.substr(number);
};

export const isCondoFiltersEmpty = (currentFilters: ICondoFiltersState): boolean => {
  return isEqual(omit(currentFilters, ['sortType']), omit(condoFiltersInitialState, ['sortType']));
};

export const getCondoFiltersCount = (currentFilters: ICondoFiltersState): number => {
  let res = ZERO;
  const objToCount: { [key: string]: (string | number | boolean)[] } = omit(currentFilters, [
    'sortType',
    'distanceInMiles',
    'propertyName',
    'isAllInclusiveOnly',
  ]);

  Object.keys(objToCount).forEach((key) => {
    if (objToCount[key] && objToCount[key].length) {
      res += objToCount[key].length;
    }
  });

  if (currentFilters.distanceInMiles !== defaultDistance) {
    res++;
  }

  if (currentFilters.isAllInclusiveOnly) {
    res++;
  }

  return res;
};

export const getCountryCode = (
  value: string,
  supplierType: string,
  condosCountriesCode: ICountriesCode,
): string => {
  return supplierType === SupplierType.Intervals
    ? condosCountriesCode.intervalCountries.find((item) => item.name === value).code
    : '';
};

export const getFullUnitName = (unit: ICondoUnit, intl: IntlShape): string => {
  return !isUndefined(unit.bedRoomType)
    ? `${unit.id} ${unit.bedRoomType} ${intl.formatMessage({ id: 'max.sleeps' })}:${unit.maxOccupancy
    }, ${intl.formatMessage({ id: 'private' })}:${unit.privacyOccupancy} ${!isUndefined(unit.bathroom)
      ? unit.bathroom === FULL
        ? `, ${startCase(toLower(unit.bathroom))} ${intl.formatMessage({ id: 'bathroom' })}`
        : `, ${unit.bathroom}`
      : ''
    }${!isUndefined(unit.kitchenType)
      ? `, ${startCase(toLower(unit.kitchenType))} ${intl.formatMessage({ id: 'kitchen' })}`
      : ''
    }`
    : `${unit.id} ${intl.formatMessage(
      { id: 'bedrooms.count' },
      { count: unit.bedroomsCount },
    )} ${intl.formatMessage({ id: 'max.sleeps' })}:${unit.maxOccupancy}, ${intl.formatMessage({
      id: 'private',
    })}:${unit.privacyOccupancy} ${!isUndefined(unit.bathroom) ? `, ${startCase(toLower(unit.bathroom))}` : ''
    }${!isUndefined(unit.kitchenType)
      ? `, ${startCase(toLower(unit.kitchenType))} ${intl.formatMessage({ id: 'kitchen' })}`
      : ''
    }`;
};
